<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 12px;">
      <h2 class="mb-0">Relatório de Contas a Pagar</h2>
      <v-btn outlined color="primary" @click="$router.push('/relatorios#financeiro')">
        <v-icon left>mdi-arrow-left</v-icon>
        Voltar ao menu
      </v-btn>
    </div>

    <div class="d-flex flex-wrap align-center mt-4 mb-4" style="gap: 12px;">
      <v-text-field v-model="filtros.dataInicio" type="date" label="Vencimento inicial" solo hide-details clearable class="c-filtro" />
      <v-text-field v-model="filtros.dataFim" type="date" label="Vencimento final" solo hide-details clearable class="c-filtro" />

      <v-autocomplete v-model="filtros.fornecedor" :items="fornecedorOptions" label="Fornecedor" clearable solo hide-details class="c-filtro" />
      <v-autocomplete v-model="filtros.status" :items="statusOptions" label="Status" clearable solo hide-details class="c-filtro" />

      <v-btn color="primary" :loading="loading" @click="buscarDados">
        <v-icon left>mdi-refresh</v-icon>
        Recarregar
      </v-btn>

      <v-menu>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" outlined v-on="on" :disabled="loading || dadosFiltrados.length === 0">
            <v-icon left>mdi-file-export-outline</v-icon>
            Exportar
          </v-btn>
        </template>
        <v-list>
          <v-list-item link @click="gerarExcel"><v-list-item-title>Excel</v-list-item-title></v-list-item>
          <v-list-item link @click="gerarPdf"><v-list-item-title>PDF</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div ref="areaPdf">
      <v-data-table :headers="headers" :items="dadosFiltrados" :loading="loading" item-key="_rowKey" rows-per-page-text="Linhas por página:" page-text="{0}-{1} de {2}" class="elevation-1">
        <template v-slot:item.dataEmissao="{ item }">{{ formatarDataPtBr(item.dataEmissao) }}</template>
        <template v-slot:item.vencimento="{ item }">{{ formatarDataPtBr(item.vencimento) }}</template>
        <template v-slot:item.valorParcela="{ item }">{{ formatarMoedaPtBr(item.valorParcela) }}</template>
        <template v-slot:item.valorTotalNota="{ item }">{{ formatarMoedaPtBr(item.valorTotalNota) }}</template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import relatorioContasAPagarService from "@/views/Relatorios/services/relatorioContasAPagarService";
import { exportarExcel, exportarPdfTabela, formatarDataPtBr, formatarMoedaPtBr, normalizarTexto } from "@/views/Relatorios/base/relatorioBaseUtils";
import relatorioPermissionMixin from "@/mixins/relatorioPermissionMixin";

export default {
  name: "RelatorioContasAPagarView",
  mixins: [relatorioPermissionMixin],
  data() {
    return {
      relatorioSchemas: ["ContasAPagar", "FinPay", "finpay"],
      relatorioFiltrarPorOwner: false,
      loading: false,
      dados: [],
      filtros: { dataInicio: "", dataFim: "", fornecedor: "", status: "Prevista" },
      headers: [
        { text: "Pedido", value: "pedido" },
        { text: "Ordem de Pagamento", value: "ordemPagamento" },
        { text: "JOB", value: "job" },
        { text: "OS", value: "os" },
        { text: "Centro de Custo", value: "centroCusto" },
        { text: "GrupoDesp", value: "grupoDesp" },
        { text: "Número NF", value: "numeroNotaFiscal" },
        { text: "Data Emissão", value: "dataEmissao" },
        { text: "Vencimento", value: "vencimento" },
        { text: "Num. Parcelas", value: "numParcelas" },
        { text: "Valor Parcela", value: "valorParcela" },
        { text: "Valor Total Nota", value: "valorTotalNota" },
        { text: "Fornecedor", value: "remetente" },
        { text: "CNPJ / CPF", value: "cnpjEmitente" },
        { text: "Natureza da Operação", value: "naturezaOperacao" },
        { text: "Informações Complementares", value: "infoComplementares" },
        { text: "Forma", value: "forma" },
        { text: "Tipo Pagamento", value: "tipoPagamento" },
        { text: "Evento", value: "evento" }
      ]
    };
  },
  computed: {
    fornecedorOptions() {
      return Array.from(new Set(this.dados.map((item) => normalizarTexto(item.remetente)).filter(Boolean))).sort();
    },
    statusOptions() {
      const opcoes = Array.from(new Set(this.dados.map((item) => normalizarTexto(item.status)).filter(Boolean))).sort();
      if (!opcoes.length) {
        return ["Prevista"];
      }
      return opcoes;
    },
    dadosFiltrados() {
      const inicio = this.filtros.dataInicio ? new Date(this.filtros.dataInicio) : null;
      const fim = this.filtros.dataFim ? new Date(this.filtros.dataFim) : null;
      if (fim) fim.setHours(23, 59, 59, 999);

      const filtrados = this.dados.filter((item) => {
        const dataLinha = item.vencimento ? new Date(item.vencimento) : null;
        const fornecedorOk = !this.filtros.fornecedor || normalizarTexto(item.remetente) === normalizarTexto(this.filtros.fornecedor);
        const statusOk = !this.filtros.status || normalizarTexto(item.status) === normalizarTexto(this.filtros.status);

        if (!dataLinha || Number.isNaN(dataLinha.getTime())) return fornecedorOk && statusOk;

        const dataInicioOk = !inicio || dataLinha >= inicio;
        const dataFimOk = !fim || dataLinha <= fim;
        return fornecedorOk && statusOk && dataInicioOk && dataFimOk;
      });

      return filtrados.map((item, index) => ({
        ...item,
        _rowKey: `${normalizarTexto(item.numeroNotaFiscal)}-${normalizarTexto(item.pedido)}-${normalizarTexto(item.remetente)}-${index}`
      }));
    }
  },
  watch: {
    'filtros.dataInicio'() { this.buscarDados(); },
    'filtros.dataFim'() { this.buscarDados(); }
  },
  methods: {
    formatarDataPtBr,
    formatarMoedaPtBr,
    async buscarDados() {
      this.loading = true;
      try {
        const lista = await relatorioContasAPagarService.listarContasAPagar(this.filtros);
        this.dados = Array.isArray(lista) ? lista : [];
      } finally {
        this.loading = false;
      }
    },
    gerarExcel() {
      exportarExcel(this.dadosFiltrados, "relatorio-contas-a-pagar.xlsx");
    },
    async gerarPdf() {
      // 25 colunas não cabem legíveis numa A4: a divisão igual de largura do jspdf-autotable
      // deixava cada coluna com poucos mm, quebrando o texto letra a letra. Passamos para A3
      // paisagem e damos uma largura própria pra cada coluna (mais espaço pras que têm texto
      // longo, menos pras que normalmente são só "-" ou números curtos).
      await exportarPdfTabela({
        headers: this.headers,
        rows: this.dadosFiltrados,
        nomeArquivo: "relatorio-contas-a-pagar.pdf",
        tituloRelatorio: "Relatório de Contas a Pagar",
        pageFormat: [420, 297],
        estilos: { fontSize: 7.5, cellPadding: 1.5 },
        // Larguras escaladas para ocupar toda a largura útil da página A3 paisagem (~398mm
        // de ~400mm disponíveis), mantendo a mesma proporção entre colunas de antes -- sem
        // isso o jspdf-autotable para no total da soma das larguras e deixa espaço em branco
        // sobrando à direita. Colunas "Empresa" (sempre vazia) e "CFOP" removidas; larguras
        // reescaladas para as colunas restantes voltarem a preencher a página toda.
        // centroCusto ganhou +8mm (agora traz a descrição do Centro de Custo do colaborador,
        // ex. "DIREÇÃO COMERCIAL", que não cabia nos 18mm originais) tirando 8mm de
        // infoComplementares (era a coluna mais folgada) para manter a soma total igual.
        larguraColunasPorCampo: {
          pedido: 18,
          ordemPagamento: 22,
          job: 18,
          os: 15,
          centroCusto: 26,
          grupoDesp: 23,
          numeroNotaFiscal: 18,
          dataEmissao: 18,
          vencimento: 18,
          numParcelas: 13,
          valorParcela: 22,
          valorTotalNota: 22,
          remetente: 31,
          cnpjEmitente: 19,
          naturezaOperacao: 22,
          infoComplementares: 26,
          forma: 18,
          tipoPagamento: 23,
          evento: 26
        },
        formatadoresPorCampo: {
          dataEmissao: (valor) => this.formatarDataPtBr(valor),
          vencimento: (valor) => this.formatarDataPtBr(valor),
          valorParcela: (valor) => this.formatarMoedaPtBr(valor),
          valorTotalNota: (valor) => this.formatarMoedaPtBr(valor)
        }
      });
    }
  },
  async mounted() {
    if (this.relatorioTemPermissao(this.relatorioSchemas)) {
      await this.buscarDados();
    }
  }
};
</script>

<style scoped>
.c-filtro { min-width: 220px; max-width: 280px; }
</style>
